import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { InferType } from 'yup';

import AddEditParticipantDrawer from './AddEditParticipantDrawer/AddEditParticipantDrawer';
import { participantSchema } from './add-edit-project-schema';
import { dateFormatter } from 'components/shared/DataGrid/utils/valueFormatters/dateFormatter';
import { nestedFieldValueGetter } from 'utils/grid';
import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';

export type ParticipantFields = InferType<typeof participantSchema>;

type ParticipantsGridProps = {
  participants: ParticipantFields[];
  isEditMode: boolean;
  append: any;
  update: any;
  remove: any;
  projectValidFrom: string;
  projectValidTo: string;
};

export default function ParticipantsGrid({
  participants,
  isEditMode,
  append,
  update,
  remove,
  projectValidFrom,
  projectValidTo
}: ParticipantsGridProps) {
  const { t } = useTranslation();
  const [openDrawer, setOpenDrawer] = useState(false);
  const [participantToEdit, setParticipantToEdit] = useState<any | null>(null);

  const columns = [
    {
      field: 'name',
      headerName: t('name'),
      flex: 1,
      minWidth: 150
    },
    {
      field: 'role',
      headerName: t('role'),
      flex: 1,
      minWidth: 150,
      valueGetter: (_, row) => nestedFieldValueGetter('role', row),
      valueFormatter: (value: string) => t(`roles.${value}`)
    },
    {
      field: 'validFrom',
      headerName: t('validFrom'),
      flex: 1,
      minWidth: 150,
      valueFormatter: dateFormatter
    },
    {
      field: 'validTo',
      headerName: t('validTo'),
      flex: 1,
      minWidth: 150,
      valueFormatter: dateFormatter
    }
  ];

  function handleAddClick() {
    setParticipantToEdit(null);
    setOpenDrawer(true);
  }

  function handleEditClick(id) {
    const participant = participants?.find((p) => p.id === id);
    setParticipantToEdit(participant);
    setOpenDrawer(true);
  }

  function handleDeleteClick(ids) {
    const idx = participants?.findIndex((r) => r.id === ids[0]);
    remove(idx);
  }

  function updateRecord(data) {
    const newData = {
      ...data,
      ...data.employee,
      entityId: participantToEdit?.entityId
    };

    const idx = participants?.findIndex((p) => p.id === participantToEdit?.id);

    update(idx, newData);
    setParticipantToEdit(null);
  }

  return (
    <TableCRUDLayout
      columns={columns}
      rows={participants}
      shouldRenderToolBar={false}
      renderFormContainer={false}
      disableActions={!isEditMode}
      editable={false}
      onAddClick={handleAddClick}
      onEditClick={handleEditClick}
      onDeleteClick={handleDeleteClick}
      confirmationTitle={t('deleteDialog.confirmationTitle')}
      confirmationBody={t('deleteDialog.confirmationBody')}
      initialState={{
        sorting: {
          sortModel: [{ field: 'name', sort: 'asc' }]
        }
      }}
      addRowDialog={
        <AddEditParticipantDrawer
          open={openDrawer}
          append={append}
          update={updateRecord}
          participant={participantToEdit}
          handleClose={() => setOpenDrawer(false)}
          projectValidFrom={projectValidFrom}
          projectValidTo={projectValidTo}
        />
      }
    />
  );
}
